import { useContext, useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import * as R from 'ramda';

import { Icon, IconButton, Tooltip } from '@atom/mui';
import { ArrowDropDownIcon, ArrowRightIcon, FolderIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { InventoryCategoryTree as InventoryCategoryTreeType } from '@atom/types/category';
import schemaUtilities from '@atom/utilities/schemaUtilities';

import InventoryPortalContext from '../InventoryPortalContext';

import InventoryCategoryTreeOptions from './InventoryCategoryTreeOptions';

import classes from './inventoryCategoryTree.module.css';

const PADDING_OFFSET = 0.75;

const styles = {
  blankStyle: {
    boxSizing: 'border-box',
    padding: '24px',
  },
};

export interface Props {
  category: InventoryCategoryTreeType;
  indentation: number;
  path: any[];
}

const InventoryCategoryTreeRow = ({ category, indentation, path }: Props) => {
  const {
    open,
    toggleOpen,
    setActiveCategory,
    activeCategory,
    resetSelectedAssets,
  } = useContext(InventoryPortalContext);
  const [overflowing, setOverflowing] = useState<boolean>(false);
  const container = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const { offsetWidth, scrollWidth } = container.current;
    setOverflowing(offsetWidth < scrollWidth);
  }, [category.name]);

  const expanded = open.has(category.id);

  const onClick = () => {
    setActiveCategory(category, path);
    resetSelectedAssets();
  };

  const onToggle = (event: any, id: string) => {
    event.stopPropagation();
    toggleOpen(id);
  };

  const arrowDirection = expanded ? ArrowDropDownIcon : ArrowRightIcon;

  const leftIcon = (
    <>
      {!R.isEmpty(category.categories) ? (
        <IconButton
          onClick={event => onToggle(event, category.id)}
          data-cy={`inventoryCategoryTreeToggle-${category.name}`}
        >
          <Icon color={colors.neutral.dim} icon={arrowDirection} />
        </IconButton>
      ) : (
        <div style={styles.blankStyle} />
      )}
      <div className={classes.folderIconContainer}>
        <Icon
          color={
            category.schemaId
              ? schemaUtilities.getSchemaColorFromColorId(category.colorId)
              : colors.neutral.gray
          }
          icon={FolderIcon}
        />
        {category.hasAssetChanges && (
          <div className={classes.folderChangesIcon} />
        )}
      </div>
    </>
  );

  const rowStyle = clsx(
    classes.folderRow,
    category.id === activeCategory.id && classes.selected,
  );

  const containerStyle = {
    paddingLeft: `calc(1rem + ${PADDING_OFFSET * indentation}rem)`,
  };

  const overflowStyle = {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    width: `calc(12rem - (1rem + ${PADDING_OFFSET * indentation}rem)`,
  };

  return (
    <>
      <div
        className={rowStyle}
        style={containerStyle}
        onClick={onClick}
        data-cy={`inventoryCategoryTreeRow-${category.name}`}
      >
        <div ref={container} className={classes.folderNameContainer}>
          {leftIcon}
          <div className={classes.rowNameContainer}>
            <div className={classes.assetName}>
              {!overflowing ? (
                category.name
              ) : (
                <Tooltip
                  lightTooltip
                  title={category.name}
                  placement="right-start"
                >
                  <p style={overflowStyle} className={classes.assetName}>
                    {category.name}
                  </p>
                </Tooltip>
              )}
            </div>
            <div className={classes.assetType}>{category.assetType}</div>
          </div>
        </div>
        {/* onClick handler prevents modal clicks from propagating up */}
        <div onClick={event => event.stopPropagation()}>
          <InventoryCategoryTreeOptions category={category} path={path} />
        </div>
      </div>
      {expanded ? (
        category.categories.map((child: any, index: number) => {
          return (
            <InventoryCategoryTreeRow
              key={child.id}
              category={child}
              indentation={indentation + 1}
              path={[...path, 'categories', index]}
            />
          );
        })
      ) : (
        <div />
      )}
    </>
  );
};

export default InventoryCategoryTreeRow;
